import { z } from 'zod';
import { nonEmptyPatch } from '../_patch.js';
import { DealStageCodeSchema } from './deal-stages.js';

export const DEAL_PUBLIC_CODE_REGEX = /^D-\d{5}$/;
export const DEAL_TITLE_MAX = 200;

/**
 * @aiPurpose wire projection of a `Deal` row (#1087). Commercial
 *   pipeline overlay on top of the Lead workspace; the manager
 *   promotes a qualified Lead into a Deal once a Client is bound.
 * @aiInvariant `publicCode` matches `/^D-\d{5}$/` minted by
 *   `deal_seq`. `managerId` and `clientId` are required (no
 *   unassigned-pool flow for Deals in v1); `leadId` is optional
 *   because admin-spawned Deals do not require a source Lead.
 */
export const DealSchema = z
  .object({
    id: z.string().uuid(),
    publicCode: z.string().regex(DEAL_PUBLIC_CODE_REGEX),
    managerId: z.string().uuid(),
    clientId: z.string().uuid(),
    leadId: z.string().uuid().nullable(),
    stageCode: DealStageCodeSchema,
    archivedAt: z.string().datetime().nullable(),
    archivedById: z.string().uuid().nullable(),
    title: z.string().min(1).max(DEAL_TITLE_MAX),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict();
export type Deal = z.infer<typeof DealSchema>;

const DealManagerRefSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string().min(1),
  })
  .strict();

const DealClientRefSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string().min(1),
  })
  .strict();

/**
 * @aiPurpose list-item projection of a `Deal` row for
 *   `GET /api/deals`. Includes denormalised manager + client name so
 *   the workspace list renders without an N+1 join chain.
 * @aiInvariant `archivedAt` is the canonical ISO timestamp when the
 *   deal is archived; `activityCount` is the count of `DealActivity`
 *   rows for this Deal — PII-safe and exists so the list can render
 *   a "recent activity" badge without a follow-up request per row.
 */
export const DealListItemSchema = z
  .object({
    id: z.string().uuid(),
    publicCode: z.string().regex(DEAL_PUBLIC_CODE_REGEX),
    title: z.string().min(1).max(DEAL_TITLE_MAX),
    stageCode: DealStageCodeSchema,
    managerId: z.string().uuid(),
    manager: DealManagerRefSchema,
    clientId: z.string().uuid(),
    client: DealClientRefSchema,
    leadId: z.string().uuid().nullable(),
    archivedAt: z.string().datetime().nullable(),
    activityCount: z.number().int().min(0),
    // WHY (#1596): opt-in count of China `Request` rows linked to this Deal
    //   AND visible to the actor — NOT a global FK count. Emitted ONLY when
    //   the detail read passes `?includeRequestCount=true`; optional so
    //   default list/detail payloads stay valid under `.strict()` for stale
    //   bundles. The reverse-read endpoint returns the matching summaries.
    requestCount: z.number().int().min(0).optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict();
export type DealListItem = z.infer<typeof DealListItemSchema>;

/**
 * @aiPurpose archive selector for the deal list — mirrors the
 *   `LeadArchiveFilterSchema` shape.
 */
export const DealArchiveFilterSchema = z.enum(['active', 'archived', 'all']);
export type DealArchiveFilter = z.infer<typeof DealArchiveFilterSchema>;

/**
 * @aiPurpose query string for `GET /api/deals` — cursor-paginated.
 * @aiInvariant `.strict()` blocks unknown keys. `managerId` honored
 *   only for admin callers; the backend silently ignores it for
 *   non-admin and applies ownership scope. `limit` clamped 1..100,
 *   default 50.
 */
export const GetDealsQuerySchema = z
  .object({
    stageCode: DealStageCodeSchema.optional(),
    managerId: z.string().uuid().optional(),
    clientId: z.string().uuid().optional(),
    archived: DealArchiveFilterSchema.default('active'),
    cursor: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
  })
  .strict();
export type GetDealsQuery = z.infer<typeof GetDealsQuerySchema>;

/**
 * @aiPurpose response envelope for `GET /api/deals`.
 * @aiInvariant `nextCursor` is `null` exactly when the underlying
 *   query returned ≤ `limit` rows.
 */
export const GetDealsResponseSchema = z
  .object({
    items: z.array(DealListItemSchema),
    nextCursor: z.string().min(1).nullable(),
  })
  .strict();
export type GetDealsResponse = z.infer<typeof GetDealsResponseSchema>;

/**
 * @aiPurpose input for `POST /api/deals` — manager-driven create.
 * @aiInvariant `.strict()` blocks unknown keys; `title` and `clientId`
 *   are required. The backend assigns `managerId = actor.sub` and
 *   `stageCode = 'qualification'`. `leadId` is optional; when
 *   supplied, visibility checks ensure the actor can see the source
 *   Lead.
 */
export const CreateDealInputSchema = z
  .object({
    title: z.string().min(1).max(DEAL_TITLE_MAX),
    clientId: z.string().uuid(),
    leadId: z.string().uuid().optional(),
  })
  .strict();
export type CreateDealInput = z.infer<typeof CreateDealInputSchema>;

/**
 * @aiPurpose input for `PATCH /api/deals/:id` — partial edit of the
 *   product-editable Deal fields. Stage transitions and lifecycle
 *   actions (claim/assign/archive/restore, lead/request linking) live
 *   on separate endpoints in #1088.
 * @aiInvariant `nonEmptyPatch` rejects `{}`. `leadId` accepts `null`
 *   so the manager can clear the source-lead link explicitly.
 *   `managerId`, `clientId`, and `stageCode` are deliberately excluded
 *   from this shape — they belong to dedicated lifecycle endpoints.
 */
const DealPatchInnerSchema = z
  .object({
    title: z.string().min(1).max(DEAL_TITLE_MAX),
    leadId: z.string().uuid().nullable(),
  })
  .strict();
export const PatchDealInputSchema = nonEmptyPatch(DealPatchInnerSchema.partial().strict());
export type PatchDealInput = z.infer<typeof PatchDealInputSchema>;

/**
 * @aiPurpose input for `PATCH /api/deals/:id/stage` — request the
 *   Deal to move into `toStage`. The stage graph is enforced
 *   server-side via `canTransitionDealStage`; an illegal target
 *   rejects with a typed `DealStageTransitionError` in #1088.
 */
export const TransitionDealStageInputSchema = z
  .object({
    toStage: DealStageCodeSchema,
  })
  .strict();
export type TransitionDealStageInput = z.infer<typeof TransitionDealStageInputSchema>;

/**
 * @aiPurpose response envelope for `POST /api/deals`. Returns the
 *   denormalised `DealListItem` shape so the workspace list can render
 *   the row immediately without a follow-up detail fetch.
 */
export const CreateDealResponseSchema = z
  .object({
    deal: DealListItemSchema,
  })
  .strict();
export type CreateDealResponse = z.infer<typeof CreateDealResponseSchema>;
